// ─────────────────────────────────────────────
// Oike — Gestión de clientes › Directorio (pure; tested by scripts/test-directory.mjs).
// One list of people from the records that already exist: the commercial contacts (Stakeholders, in the scope of whoever
// is signed in) and, only for the coach, the participants' cards (Coaching People). Rules:
// - A contact and a card share one row only when the card is explicitly linked to that contact by id
//   (Coaching People › Stakeholder Id) and no other card claims the same contact.
// - Several cards linked to one contact, or a card linked to a contact that is not available, are flagged for review:
//   nothing is chosen, joined or fixed here.
// - A repeated email is only a warning, never proof of identity.
// - A person has no single permanent company: a contact's company is shown as an attribute of that contact record.
// - Without permission to see the participants (`people` null), there are no participant marks, counts or states.
// Nothing is written; each row opens the existing card(s).
// ─────────────────────────────────────────────

const REC = /^rec[A-Za-z0-9]{14}$/;
const name = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v.name ?? '' : v ?? '');
const links = (v) => (Array.isArray(v) ? v.map(x => (typeof x === 'string' ? x : x?.id)).filter(Boolean) : []);
export const normEmail = (e) => String(e || '').trim().toLowerCase();

export const FLAG_TEXT = {
  multiple_links: 'Varias fichas de participante vinculadas al mismo contacto: revisar',
  contact_missing: 'Su contacto vinculado no está disponible: revisar el vínculo',
  email_repeated: 'Email repetido en otra fila: es solo una advertencia, no prueba que sea la misma persona',
};

/** The contact as the directory shows it (only identifying data). */
function contactOf(s, accountNames) {
  const f = s.fields || {};
  return {
    id: s.id, name: `${name(f['Name'])} ${name(f['Last name'])}`.trim() || 'Contacto sin nombre', email: normEmail(f['Email']),
    companies: links(f['Account']).map(id => accountNames.get(id) || null).filter(Boolean), role: name(f['Job Title'] || f['Role']) || '',
  };
}

/**
 * contacts: Stakeholders records; accounts: Accounts records (for the names only); people: the coach's cards
 * [{ id, name, email, stakeholderId }] or null when this login may not see them.
 * Returns rows { key, contact?, person?, name, email, emails, flags } sorted by name.
 */
export function directoryRows({ contacts = [], accounts = [], people = null }) {
  const accountNames = new Map(accounts.map(a => [a.id, String(a.fields?.['Account Name'] || '').trim()]));
  const cs = contacts.map(s => contactOf(s, accountNames));
  const contactIds = new Set(cs.map(c => c.id));
  const byContact = new Map();
  for (const p of people || []) if (p.stakeholderId && REC.test(p.stakeholderId)) byContact.set(p.stakeholderId, [...(byContact.get(p.stakeholderId) || []), p]);
  const rows = [];
  const paired = new Set();
  for (const c of cs) {
    const linked = byContact.get(c.id) || [];
    if (linked.length === 1) { paired.add(linked[0].id); rows.push({ key: `pair:${c.id}:${linked[0].id}`, contact: c, person: personOf(linked[0]), flags: [] }); }
    else rows.push({ key: `contact:${c.id}`, contact: c, person: null, flags: linked.length > 1 ? ['multiple_links'] : [] });
  }
  for (const p of people || []) {
    if (paired.has(p.id)) continue;
    const flags = [];
    if (p.stakeholderId && (byContact.get(p.stakeholderId) || []).length > 1) flags.push('multiple_links');
    else if (p.stakeholderId && !contactIds.has(p.stakeholderId)) flags.push('contact_missing');
    rows.push({ key: `person:${p.id}`, contact: null, person: personOf(p), flags });
  }
  // A repeated email (across rows) is a warning only
  const count = new Map();
  for (const r of rows) for (const e of emailsOf(r)) count.set(e, (count.get(e) || 0) + 1);
  for (const r of rows) if (emailsOf(r).some(e => count.get(e) > 1)) r.flags.push('email_repeated');
  for (const r of rows) { r.name = r.person?.name && !r.contact ? r.person.name : r.contact?.name || r.person?.name || ''; r.emails = emailsOf(r); r.email = r.emails[0] || ''; }
  return rows.sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
}
const personOf = (p) => ({ id: p.id, name: String(p.name || '').trim() || 'Persona sin nombre', email: normEmail(p.email), stakeholderId: p.stakeholderId || null });
const emailsOf = (r) => [...new Set([r.contact?.email, r.person?.email].filter(Boolean))];

/** Rows matching a search (name, email or the contact's company) and a filter: all, contacts, participants, review. */
export function filterRows(rows, { q = '', filter = 'all' } = {}) {
  const words = String(q).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').split(/\s+/).filter(Boolean);
  const text = (r) => `${r.name} ${r.emails.join(' ')} ${(r.contact?.companies || []).join(' ')}`.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  return rows.filter(r => (filter === 'all' || (filter === 'contacts' && r.contact) || (filter === 'participants' && r.person) || (filter === 'review' && r.flags.length))
    && words.every(w => text(r).includes(w)));
}

/**
 * The unified client file of one person: which contact and which participant's card it shows, only through an explicit
 * link by id (a card's Stakeholder Id pointing to that contact, claimed by no other card). Never by name or email; a
 * contradiction is returned as a flag and nothing is chosen. `contacts` are Stakeholders records; `people` the coach's
 * cards, or null without permission (then no card, no flag about cards). Returns { contact, person, flags, others }.
 */
export function resolveClient({ contactId = '', personId = '', contacts = [], people = null }) {
  const contact = contactId ? contacts.find(c => c.id === contactId) || null : null;
  const cards = Array.isArray(people) ? people : null;
  const flags = []; let others = [];
  let person = personId && cards ? cards.find(p => p.id === personId) || null : null;
  const claim = (sid) => (cards || []).filter(p => p.stakeholderId === sid);
  if (contactId && personId && person && person.stakeholderId !== contactId) {
    // Asked for both but they are not linked: the contact's file, and the card only named for review
    flags.push('not_linked'); others = [person]; person = null;
  }
  if (contact && !person && cards && !flags.length) {
    const linked = claim(contact.id);
    if (linked.length === 1) person = linked[0];
    else if (linked.length > 1) { flags.push('multiple_links'); others = linked; }
  }
  let linkedContact = contact;
  if (person && !contact && person.stakeholderId) {
    const all = claim(person.stakeholderId);
    if (all.length > 1) { flags.push('multiple_links'); others = all.filter(p => p.id !== person.id); }
    else if (contactId) flags.push('contact_missing');
    else linkedContact = contacts.find(c => c.id === person.stakeholderId) || null;
    if (!linkedContact && !flags.length) flags.push('contact_missing');
  }
  if (person && contact && claim(contact.id).length > 1) { flags.push('multiple_links'); others = claim(contact.id).filter(p => p.id !== person.id); }
  return { contact: linkedContact, person, flags, others: others.map(p => ({ id: p.id, name: p.name })) };
}
